import { useMemo } from 'react';

import { useSession } from '@platform/api-client/react';

/**
 * Reading the caller's permissions (M2.8, rules §17.2, §22.2).
 *
 * `GET /auth/session` returns the permission list so the console can hide controls the
 * operator cannot use. **That is all it is for.** Every endpoint behind every one of
 * these controls checks the same permission on the server, and a hidden button is never
 * the enforcement point - rules §22.2 says so in as many words, and rule §22.5 is why
 * `can()` answers `false` while the session is still loading.
 */

export interface Permissions {
  /** Whether the caller holds a permission. `false` until the session has loaded. */
  can: (key: string) => boolean;
  /** Whether the caller holds every one of these. An empty list is always true. */
  canAll: (keys: readonly string[]) => boolean;
  /** Whether the caller holds at least one. An empty list is always true. */
  canAny: (keys: readonly string[]) => boolean;
  /** Still fetching; a guard should wait rather than decide. */
  isPending: boolean;
}

export function usePermissions(): Permissions {
  const session = useSession();
  const keys = session.data?.permissions;

  return useMemo<Permissions>(() => {
    const held = new Set<string>(keys ?? []);
    const can = (key: string): boolean => held.has(key);
    return {
      can,
      canAll: (list) => list.every(can),
      canAny: (list) => list.length === 0 || list.some(can),
      isPending: session.isPending,
    };
  }, [keys, session.isPending]);
}

/** The nav keys, in one place so the shell and the tests agree on them. */
export const PLATFORM_PERMISSIONS = {
  tenantRead: 'platform.tenant.read',
  catalogManage: 'platform.catalog.manage',
  usersRead: 'platform.users.read',
  logsRead: 'platform.logs.read',
  plansManage: 'platform.plans.manage',
  settingsManage: 'platform.settings.manage',
  impersonate: 'platform.impersonate',
} as const;
